import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { FileText, CheckCircle, XCircle, Clock, AlertCircle, Users, Building2 } from 'lucide-react';
import { usePaymentStats } from '../hooks/usePayments';
import { useRegistrationStats } from '../hooks/useRegistrations';
import { useCenters } from '../hooks/useCenters';

const StatCard = ({ icon: Icon, label, value, color, loading }) => (
  <div className="bg-white rounded-lg shadow p-6 border-l-4" style={{ borderColor: color }}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-600 text-sm mb-1">{label}</p>
        {loading ? (
          <div className="h-8 bg-gray-200 rounded animate-pulse w-20" />
        ) : (
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        )}
      </div>
      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { stats: paymentStats, loading: paymentsLoading } = usePaymentStats();
  const { stats: registrationStats, loading: registrationsLoading } = useRegistrationStats();
  const { centers, loading: centersLoading } = useCenters();
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    if (paymentStats) {
      setChartData([
        { name: 'Approved', value: paymentStats.approved_count || 0 },
        { name: 'Pending', value: paymentStats.pending_count || 0 },
        { name: 'Rejected', value: paymentStats.rejected_count || 0 },
        { name: 'Needs Info', value: paymentStats.needs_info_count || 0 },
      ]);
    }
  }, [paymentStats]);

  const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6'];
  const loading = paymentsLoading || registrationsLoading || centersLoading;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of parent registrations and payment submissions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Pending Registrations" value={registrationStats?.pending_count || 0} color="#8b5cf6" loading={loading} />
        <StatCard icon={FileText} label="Total Payments" value={paymentStats?.total_payments || 0} color="#3b82f6" loading={loading} />
        <StatCard icon={Clock} label="Pending Payments" value={paymentStats?.pending_count || 0} color="#f59e0b" loading={loading} />
        <StatCard icon={CheckCircle} label="Approved Payments" value={paymentStats?.approved_count || 0} color="#10b981" loading={loading} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon={AlertCircle} label="Needs Info" value={paymentStats?.needs_info_count || 0} color="#3b82f6" loading={loading} />
        <StatCard icon={XCircle} label="Rejected Payments" value={paymentStats?.rejected_count || 0} color="#ef4444" loading={loading} />
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600 text-sm mb-1">Approved Parents</p>
          <p className="text-3xl font-bold text-gray-900">
            {loading ? '...' : registrationStats?.approved_count || 0}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {registrationStats?.total_parents || 0} parents total
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Amount Overview</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Total Approved</p>
              <p className="text-2xl font-bold text-green-600">
                {paymentStats?.total_amount_approved ? Number(paymentStats.total_amount_approved).toFixed(2) : '0.00'} Birr
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Pending</p>
              <p className="text-2xl font-bold text-yellow-600">
                {paymentStats?.total_amount_pending ? Number(paymentStats.total_amount_pending).toFixed(2) : '0.00'} Birr
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Payment Approval Rate</p>
              <p className="text-xl font-bold text-gray-900">
                {(paymentStats?.approval_rate || 0).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Distribution</h3>
          {chartData.some((item) => item.value > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value">
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-sm py-16 text-center">No payment data yet</p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">By Learning Center</h3>
        {centers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Center</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Parents</th>
                  <th className="py-2">Payments</th>
                </tr>
              </thead>
              <tbody>
                {centers.map((center) => (
                  <tr key={center.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-medium text-gray-900">{center.name}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${center.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {center.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 pr-4">{center.user_count}</td>
                    <td className="py-3">{center.payment_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No centers configured yet. Add centers from the Centers page.</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <button
            onClick={() => navigate('/dashboard/centers')}
            className="p-4 bg-indigo-50 hover:bg-indigo-100 rounded-lg text-left transition-colors"
          >
            <Building2 className="w-6 h-6 mb-2 text-indigo-600" />
            <p className="text-sm font-medium text-gray-900">Manage Centers</p>
            <p className="text-xs text-gray-600 mt-1">{centers.length} center(s)</p>
          </button>
          <button
            onClick={() => navigate('/dashboard/registrations')}
            className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg text-left transition-colors"
          >
            <Users className="w-6 h-6 mb-2 text-purple-600" />
            <p className="text-sm font-medium text-gray-900">Review Registrations</p>
            <p className="text-xs text-gray-600 mt-1">{registrationStats?.pending_count || 0} pending</p>
          </button>
          <button
            onClick={() => navigate('/dashboard/payments')}
            className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg text-left transition-colors"
          >
            <FileText className="w-6 h-6 mb-2 text-blue-600" />
            <p className="text-sm font-medium text-gray-900">Review Payments</p>
            <p className="text-xs text-gray-600 mt-1">{paymentStats?.pending_count || 0} pending</p>
          </button>
          <button
            onClick={() => navigate('/dashboard/payments')}
            className="p-4 bg-green-50 hover:bg-green-100 rounded-lg text-left transition-colors"
          >
            <CheckCircle className="w-6 h-6 mb-2 text-green-600" />
            <p className="text-sm font-medium text-gray-900">Approved Payments</p>
            <p className="text-xs text-gray-600 mt-1">{paymentStats?.approved_count || 0} approved</p>
          </button>
        </div>
      </div>
    </div>
  );
}
